import React from 'react';
import * as Sentry from '@sentry/react';
import { Button, Dialog, Dialogs, Flex, Text } from '@leverege/ui-elements';
import { CodeInput, CodeScanner } from '@leverege/ui-scanner-elements';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { ValueCache } from '@leverege/value-cache';
import { GlobalState } from '@leverege/ui-redux';
import { Config } from '@leverege/plugin';
import S from '../pairing/Scan.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default class Scan extends React.Component {
  constructor(props) {
    super(props);
    this.cache = new ValueCache();
    this.state = {
      scanning: true
    };
  }
  onVINScan = evt => {
    const {
      value: {
        codeResult: {
          code
        }
      },
      data
    } = evt;
    this.onScan({
      value: {
        text: code
      },
      data
    });
  };
  onScan = async evt => {
    const {
      onChange,
      actions,
      eventData,
      parentNetworkId,
      networkId
    } = this.props;
    this.setState({
      scanning: false
    });
    const text = Array.isArray(evt.value) ? evt.value[0].text : evt.value.text;
    const isParent = eventData === 'assetId';
    let nwkId;
    if (isParent) {
      nwkId = parentNetworkId;
    } else {
      nwkId = networkId;
    }
    const aliasKey = Config.get('BeaconNetwork', 'aliasKey', 'deviceId');
    let data;
    const lowerCaseText = text?.toLowerCase();
    if (lowerCaseText) {
      try {
        data = (await GlobalState.dispatch(actions.search({
          limit: 100,
          filter: {
            type: 'logical',
            operator: 'and',
            conditions: [{
              type: 'equals',
              field: `networkAliases/${nwkId}/${aliasKey}`,
              fieldType: 'string',
              value: [lowerCaseText]
            }]
          }
        }, null, 'scanSearch')))?.items?.[0];
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      }
      if (!data) {
        this.onNotFound(text);
        return;
      }
    }
    onChange({
      data: evt.data,
      value: lowerCaseText,
      original: data
    });
  };
  onError = ({
    error
  }) => {
    Dialogs.show({
      component: props => {
        const onTryAgain = () => {
          props.onTryAgain();
          props.onClose();
        };
        const onCancel = () => {
          props.onCancel();
          props.onClose();
        };
        return /*#__PURE__*/_jsx(NotFoundDialog, {
          ...props,
          onTryAgain: onTryAgain,
          onCancel: onCancel
        });
      },
      props: {
        ...this.props,
        error,
        onTryAgain: this.onTryAgain,
        onCancel: this.onCancel
      }
    });
  };
  onNotFound = value => {
    Dialogs.show({
      component: props => {
        const onTryAgain = () => {
          props.onTryAgain();
          props.onClose();
        };
        const onCancel = () => {
          props.onCancel();
          props.onClose();
        };
        return /*#__PURE__*/_jsx(NotFoundDialog, {
          ...props,
          onTryAgain: onTryAgain,
          onCancel: onCancel
        });
      },
      props: {
        ...this.props,
        value,
        onTryAgain: this.onTryAgain,
        onCancel: this.onCancel
      }
    });
  };
  onCancel = () => {
    const {
      onClick
    } = this.props;
    onClick({
      data: {
        screen: 'main',
        field: null
      }
    });
  };
  onTryAgain = () => {
    this.setState({
      scanning: true
    });
  };
  getZxOpts = () => {
    const opts = new Map();
    opts.set(DecodeHintType.TRY_HARDER, true);
    opts.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    return opts;
  };
  render() {
    const {
      eventData,
      onClick
    } = this.props;
    const {
      scanning
    } = this.state;
    const zxOpts = this.cache.calc(this.getZxOpts, eventData);
    return /*#__PURE__*/_jsxs("div", {
      className: S.scannerContainer,
      children: [/*#__PURE__*/_jsx(Button, {
        icon: "fa fa-times fa-fw",
        onClick: onClick,
        eventData: {
          screen: 'main',
          field: null
        },
        className: S.scannerClose,
        variant: "secondaryIconic"
      }), /*#__PURE__*/_jsx(CodeScanner, {
        scanning: scanning,
        zxOpts: zxOpts,
        scanMilliseconds: 500,
        className: S.scanner,
        onChange: this.onScan,
        eventData: eventData,
        width: "100%",
        height: "100%",
        style: {
          objectFit: 'cover'
        }
      }), /*#__PURE__*/_jsx(CodeInput, {
        zxOpts: zxOpts,
        variant: "upload",
        onChange: this.onScan,
        onError: this.onError,
        eventData: eventData,
        className: S.scannerUpload,
        children: "Upload"
      })]
    });
  }
}
function NotFoundDialog(props) {
  const {
    onCancel,
    onTryAgain,
    show,
    eventData,
    error
  } = props;
  const isParent = eventData === 'assetId';
  const deviceName = error ? 'Barcode' : isParent ? 'Zone' : 'Beacon';
  const message = error ? 'No barcode could be detected in the uploaded image. Please try again with a different image or enter the code manually' : isParent ? `If you believe that this zone already exists in the system, please try again. 
    Otherwise, proceed to add zone.` : `Please try scanning this beacon again or manually entering the beacon ID. 
    If failure persists, contact your dealership manager.`;
  return /*#__PURE__*/_jsx(Dialog, {
    show: show,
    showBackground: false,
    eventData: {
      screen: 'main',
      field: null
    },
    onClose: onCancel,
    className: S.notFound,
    children: /*#__PURE__*/_jsxs(Flex, {
      variant: "colM",
      align: "center",
      children: [/*#__PURE__*/_jsx("img", {
        width: 120,
        height: 120,
        alt: "No Device",
        src: "https://recovr-ui-assets.storage.googleapis.com/no_device.png"
      }), /*#__PURE__*/_jsxs(Text, {
        variant: "formTitle",
        children: [deviceName, " Not Found"]
      }), /*#__PURE__*/_jsx(Text, {
        variant: "bodyCentered",
        children: message
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onTryAgain,
        className: S.fullWidth,
        variant: "primary",
        children: "Try Again"
      }), /*#__PURE__*/_jsx(Button, {
        className: S.fullWidth,
        variant: "link",
        eventData: {
          screen: 'main',
          field: null
        },
        onClick: onCancel,
        children: "Cancel"
      })]
    })
  });
}